import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PersonaRow } from "@/components/experience/persona-row";

interface CastPersonaDialogProps {
  /** Controlled open state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Project the scenario belongs to — used to load the available persona pool. */
  projectId: string;
  /** Outline (UI: scenario) the new persona binding belongs to. */
  outlineId: string;
  /** Personas already cast on this scenario — excluded from the picker. */
  excludePersonaIds?: string[];
  /** Optional callback fired after the new binding is created. */
  onCast?: (createdBindingId: string | null) => void;
}

/**
 * CastPersonaDialog — the single source for adding a persona binding to
 * a scenario. Used by the scenarios list `⋯` action menu and by the
 * scenario editor's "Add a persona" affordance, so both surfaces stay
 * in sync as the cast flow evolves.
 *
 * Loads the project's personas, hides any already cast on this scenario,
 * lets the user pick one, and creates the (outline, persona) binding via
 * `trpc.scenario.create`. Resets internal state when closed.
 */
export function CastPersonaDialog({
  open,
  onOpenChange,
  projectId,
  outlineId,
  excludePersonaIds = [],
  onCast,
}: CastPersonaDialogProps) {
  const [castPersona, setCastPersona] = useState<string>("");
  const [brief, setBrief] = useState<string>("");
  const utils = trpc.useUtils();

  const { data: personasList } = trpc.persona.list.useQuery(
    { projectId },
    { enabled: !!projectId && open },
  );

  const excluded = useMemo(
    () => new Set(excludePersonaIds),
    [excludePersonaIds],
  );

  const castablePersonas = useMemo(
    () => (personasList ?? []).filter((p) => !excluded.has(p.id)),
    [personasList, excluded],
  );

  const selectedPersona = useMemo(
    () => castablePersonas.find((p) => p.id === castPersona),
    [castablePersonas, castPersona],
  );

  const createScenario = trpc.scenario.create.useMutation({
    onSuccess: (created) => {
      void utils.outline.getById.invalidate({ id: outlineId });
      void utils.outline.list.invalidate();
      if (castPersona) {
        void utils.persona.getById.invalidate({ id: castPersona });
      }
      const personaName =
        castablePersonas.find((p) => p.id === castPersona)?.name ?? "Persona";
      toast.success(`${personaName} cast`);
      const newBindingId =
        (created as { id?: string } | null | undefined)?.id ?? null;
      onCast?.(newBindingId);
      onOpenChange(false);
    },
    onError: (err) => toastTrpcError(err, "Couldn't cast persona"),
  });

  // Reset selection whenever the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setCastPersona("");
      setBrief("");
    }
  }, [open]);

  const noPersonas = personasList && personasList.length === 0;
  const noEligiblePersonas =
    personasList && personasList.length > 0 && castablePersonas.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cast a persona</DialogTitle>
          <DialogDescription>
            Gives this scenario a runnable persona lens — the same situation
            seen through that person's eyes.
          </DialogDescription>
        </DialogHeader>

        {noPersonas ? (
          <p className="text-sm text-muted-foreground">
            No personas in this project yet. Create one from the Personas page
            first.
          </p>
        ) : noEligiblePersonas ? (
          <p className="text-sm text-muted-foreground">
            Every persona in this project is already cast on this scenario.
          </p>
        ) : (
          <div className="min-w-0 space-y-4">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="cast-persona-select">Persona</Label>
              <Select value={castPersona} onValueChange={setCastPersona}>
                <SelectTrigger
                  id="cast-persona-select"
                  className="h-auto min-h-10 w-full min-w-0 max-w-full overflow-hidden py-1.5 *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:overflow-hidden"
                >
                  <SelectValue placeholder="Select a persona" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]"
                >
                  {castablePersonas.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      className="min-w-0 max-w-full overflow-hidden *:[span]:last:min-w-0 *:[span]:last:flex-1 *:[span]:last:overflow-hidden"
                    >
                      <PersonaRow
                        variant="compact"
                        persona={{
                          id: p.id,
                          name: p.name,
                          role: p.role,
                          brief: p.brief,
                        }}
                        subtitle={p.brief ? `${p.role} — ${p.brief}` : p.role}
                        size="sm"
                        className="max-w-full overflow-hidden"
                      />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {castPersona && (
              <div className="space-y-1.5">
                <Label htmlFor="cast-persona-brief">
                  First-person brief (optional)
                </Label>
                <Textarea
                  id="cast-persona-brief"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={3}
                  placeholder={`Write as ${selectedPersona?.name.split(" ")[0] ?? "this persona"} in first person — what brought them here and what's on their mind...`}
                />
                <p className="text-xs text-muted-foreground">
                  How this persona experiences the situation in their own voice.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!castPersona || !outlineId) return;
              createScenario.mutate({
                outlineId,
                personaId: castPersona,
                brief: brief.trim() || undefined,
              });
            }}
            disabled={
              !castPersona || createScenario.isPending || !!noEligiblePersonas
            }
          >
            {createScenario.isPending ? "Casting…" : "Cast persona"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
